import React, { useState, useEffect } from "react";
import { Grid, Tab, Tabs, Typography } from "@mui/material";
import { setsConfigAnalysisTabs } from "../../utils/constants";
import {
  MAutocomplete,
  MTextField,
  MDatePicker,
  TabPanel,
  ResponsiveFormContainer,
} from "@app/components";
import { useEquipmentMide, useUser } from "@services/servicesGenericsTables";
import { MODES_OPTIONS } from "@app/types/generics.types";
import ChartAbsorptionForm from "./chartAbsorption";
import { useFormContext } from "react-hook-form";
import { useAdjustmentCurve } from "../../utils/useAdjustmentCurve";

interface AnalysisFormProps {
  mode: string;
}

const AnalysisTritioForm = ({ mode }: AnalysisFormProps) => {
  const { userList } = useUser();
  const [tabValue, setTabValue] = useState(0);
  const isView = mode === MODES_OPTIONS.VIEW;
  const { equipmentMideListFiltered, loading } = useEquipmentMide();
  const { watch, setValue, getValues } = useFormContext();
  const { loadingCurve } = useAdjustmentCurve();

  useEffect(() => {
    if (getValues("equipment")) return;
    const equipment = equipmentMideListFiltered.find(
      (equipment) => equipment.codigo === "3002" //CODIGO DE EQUIPO DE CENTELLEO
    );
    setValue("equipment", equipment || undefined);
  }, [equipmentMideListFiltered]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <>
      <Tabs
        value={tabValue}
        onChange={handleChange}
        aria-label="Fields uncertainty tabs"
      >
        {setsConfigAnalysisTabs.map((set, index) => (
          <Tab key={index} label={set.label} />
        ))}
      </Tabs>
      <TabPanel value={tabValue} index={0} padding={1}>
        <ResponsiveFormContainer columns={4}>
          <MAutocomplete
            name="analyst"
            options={userList}
            label="Seleccione un analista"
            getOptionLabel={(option) => option.username || ""}
            validation={{ required: "El analista es requerido" }}
            disabled={isView}
          />
          <MDatePicker
            name="date"
            pickerType="date"
            label="Fecha calibración"
            validation={{ required: "La fecha es requerida" }}
            disabled={isView}
          />

          <MAutocomplete
            key={JSON.stringify(watch("equipment"))}
            name="equipment"
            options={equipmentMideListFiltered}
            label="Contador centelleo líquido (Tritio)"
            getOptionLabel={(option) =>
              `${option.codigo} - ${option.descripcion}` || ""
            }
            loading={loading}
            disabled={isView}
          />
        </ResponsiveFormContainer>

        <Grid item xs={12} my={2}>
          <Typography variant="tagText">{"Datos patrón primario"}</Typography>
        </Grid>
        <ResponsiveFormContainer columns={2}>
          <MTextField
            name="ta0"
            label="Actividad del patrón primario (Bq/mg)"
            placeholder="Actividad del patrón primario (Bq/mg)"
            type="number"
            validation={{ required: "La actividad es requerida" }}
            fullWidth
            disabled={isView}
          />
          <MTextField
            name="tua0"
            label="Incertidumbre patrón primario para k=2 (Bq/mg)"
            helperText="(*) Aplica los datos del certificado de la fuente patrón"
            placeholder="Incertidumbre patrón primario para k=2 (Bq/mg)"
            type="number"
            validation={{ required: "La incertidumbre es requerida" }}
            fullWidth
            disabled={isView}
          />
        </ResponsiveFormContainer>

        <Grid item xs={12} my={2}>
          <Typography variant="tagText">
            {"Datos preparación patrón secundario"}
          </Typography>
        </Grid>
        <ResponsiveFormContainer columns={2}>
          <MTextField
            name="tmpp"
            label="Masa patrón primario (mpp) (mg)"
            placeholder="Masa patrón primario (mpp) (mg)"
            type="number"
            validation={{ required: "La masa es requerida" }}
            fullWidth
            disabled={isView}
          />
          <MTextField
            name="tmps"
            label="Masa patrón secundario (mps) (mg)"
            placeholder="Masa patrón secundario (mps) (mg)"
            type="number"
            validation={{ required: "La masa es requerida" }}
            fullWidth
            disabled={isView}
          />
        </ResponsiveFormContainer>

        <Grid item xs={12} my={2}>
          <Typography variant="tagText">
            {"Datos preparación fuente de calibración"}
          </Typography>
        </Grid>
        <ResponsiveFormContainer columns={2}>
          <MTextField
            name="tmpsfcal"
            label="Masa del patrón secundario en la fuente de calibración con mayor cantidad de centellador (mg)"
            placeholder="Masa del patrón secundario en la fuente de calibración con mayor cantidad de centellador (mg)"
            type="number"
            validation={{ required: "La masa es requerida" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="tdpmg"
            label="Actividad del patrón secundario en la f. c. con mayor cantidad de centellador a fecha de contaje (dpm)"
            placeholder="Actividad del patrón secundario en la f. c. con mayor cantidad de centellador a fecha de contaje (dpm)"
            type="number"
            validation={{ required: "La actividad es requerida" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="tef"
            label="Eficiencia de la fuente de calibración con mayor cantidad de centellador"
            placeholder="Eficiencia de la fuente de calibración con mayor cantidad de centellador"
            type="number"
            validation={{ required: "La eficiencia es requerida" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="tcpmps"
            label="cpm de la fuente de calibración con mayor cantidad de centellador"
            placeholder="cpm de la fuente de calibración con mayor cantidad de centellador"
            type="number"
            validation={{ required: "La cpm es requerida" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
          <MTextField
            name="tcal"
            label="Tiempo de contaje de la fuente de calibración con mayor cantidad de centellador"
            placeholder="Tiempo de contaje de la fuente de calibración con mayor cantidad de centellador"
            type="number"
            validation={{ required: "La cpm es requerida" }}
            fullWidth
            disabled={isView}
            step={0.01}
          />
        </ResponsiveFormContainer>

        <ResponsiveFormContainer columns={2} mt={2}>
          <MTextField
            name="internal_comments"
            label="Comentarios Internos"
            placeholder="Comentarios Internos"
            type="textArea"
            rows={4}
            multiline
            fullWidth
            disabled={isView}
            step={0.01}
          />
        </ResponsiveFormContainer>
      </TabPanel>
      <TabPanel value={tabValue} index={1} padding={1}>
        {!loadingCurve && <ChartAbsorptionForm mode={mode} />}
      </TabPanel>
    </>
  );
};

export default AnalysisTritioForm;
